import { Inject, Injectable } from '@angular/core'
import { BootstrapData } from 'elements-sdk'
import randomBytes from 'randombytes'
import { Observable, Subject } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { APIService, BOOTSTRAP_DATA } from 'api/module'
import { SocketService } from './socket.service'

const initiatorID = randomBytes(16).toString('hex')

abstract class BaseStorageService {
  private changed$ = new Subject<string>()
  protected values: Record<string, any> = {}
  protected isUser = false

  constructor (
    protected api: APIService,
    protected socket: SocketService,
  ) {
    socket.subject('shared-storage:updated').subscribe(async data => {
      if (this.isUser !== !!data.user) {
        return
      }
      if (data.initiator !== initiatorID) {
        this.values[data.id] = await this.queryGet(data.id)
        this.changed$.next(data.id)
      }
    })
  }

  observe (key: string): Observable<any> {
    return this.changed$.pipe(filter(k => k === key), map(() => this.values[key]))
  }

  get (id: string, def?: any): any {
    return this.values[id] ?? def
  }

  protected abstract queryGet <T> (id: string, def?: T): Promise<T|undefined>
}

@Injectable({ providedIn: 'root' })
export class SharedStorageService extends BaseStorageService {
  constructor (
    api: APIService,
    socket: SocketService,
    @Inject(BOOTSTRAP_DATA) bootstrapData: BootstrapData,
  ) {
    super(api, socket)
    this.values = bootstrapData.sharedStorageValues
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async queryGet (id: string, def?: any): Promise<any> {
    try {
      const resp = await this.api.sharedStorage.getSharedStorageValue({
        name: id,
      })
      return resp.value ?? def
    } catch {
      return def
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async querySet (id: string, value: any): Promise<void> {
    this.values[id] = value
    await this.api.sharedStorage.setSharedStorageValue({
      name: id,
      storageRequest: {
        initiator: initiatorID,
        value,
      },
    })
  }
}

@Injectable({ providedIn: 'root' })
export class UserStorageService extends BaseStorageService {
  protected isUser = true

  constructor (
    api: APIService,
    socket: SocketService,
    @Inject(BOOTSTRAP_DATA) bootstrapData: BootstrapData,
  ) {
    super(api, socket)
    this.values = bootstrapData.userStorageValues
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async queryGet (id: string, def?: any): Promise<any> {
    try {
      const resp = await this.api.sharedStorage.getUserStorageValue({
        name: id,
      })
      return resp.value ?? def
    } catch {
      return def
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async querySet (id: string, value: any): Promise<void> {
    this.values[id] = value
    await this.api.sharedStorage.setUserStorageValue({
      name: id,
      storageRequest: {
        initiator: initiatorID,
        value,
      },
    })
  }
}
